import React, { useEffect, useState } from "react";
import axios from "axios";
import { useParams, useNavigate } from "react-router-dom";
import Swal from 'sweetalert2';
import { API_BASE_URL } from "../config/api";
import { 
  FaShoppingBag, 
  FaArrowLeft, 
  FaTruck,
  FaUndoAlt
} from "react-icons/fa";

const getImageUrl = (imagePath) => {
  if (!imagePath) return "";

  const cleanPath = String(imagePath)
    .replace(/\\/g, "/")
    .replace(/^\/+/, "");

  if (cleanPath.startsWith("http")) return cleanPath;
  if (cleanPath.startsWith("uploads/")) return `${API_BASE_URL}/${cleanPath}`;

  return `${API_BASE_URL}/uploads/${cleanPath}`;
};

const getUniformImages = (uniform) => {
  const savedImages = Array.isArray(uniform?.images) ? uniform.images : [];
  const imagePaths = [...savedImages, uniform?.image].filter(Boolean);

  return [...new Set(imagePaths)].map(getImageUrl).filter(Boolean);
};

function UniformDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [uniform, setUniform] = useState(null);
  const [size, setSize] = useState("");
  const [quantity, setQuantity] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  
  // Side images handle karne ke liye state (Currently same image repeat hogi thumbnails mein)
  const [activeImage, setActiveImage] = useState("");

  useEffect(() => {
    loadUniform();
    window.scrollTo(0, 0);
  }, [id]);

  const loadUniform = async () => {
    setLoading(true);
    setError("");
    setUniform(null);
    try {
      const res = await axios.get(`${API_BASE_URL}/uniform/single/${id}`);
      const fetchedUniform = res.data;

      if (!fetchedUniform || !fetchedUniform._id) {
        throw new Error("Uniform not found");
      }

      setUniform(fetchedUniform);
      setActiveImage(getUniformImages(fetchedUniform)[0] || "");
    } catch (err) { 
      console.error(err); 
      setError("This uniform could not be loaded.");
    } finally { 
      setLoading(false); 
    }
  };

  const addToCart = async () => {
    if (!uniform) return;

    const parentId = localStorage.getItem("parentId");
    const childId = localStorage.getItem("activeChildId");

    if (!size) {
      Swal.fire({
        icon: 'warning',
        title: 'Select Size',
        text: 'Please choose a size before adding to bag.',
        confirmButtonColor: '#097969'
      });
      return;
    }
    
    try {
      await axios.post(`${API_BASE_URL}/cart/add`, {
        parentId, childId, uniformId: uniform._id, size, quantity
      });
      window.dispatchEvent(new Event("cartUpdate"));
      Swal.fire({
        icon: 'success',
        title: 'Added to Bag!',
        text: `${uniform.name} has been added.`,
        showConfirmButton: false,
        timer: 2000
      });
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Error', text: 'Failed to add item.' });
    }
  };

  if (loading) return <div className="loader-container"><div className="pulse-loader"></div></div>;

  if (error || !uniform) {
    return (
      <div className="detail-empty-state">
        <div className="detail-empty-card">
          <h2>Uniform not found</h2>
          <p>{error || "This uniform is unavailable right now."}</p>
          <div className="detail-empty-actions">
            <button onClick={() => navigate(-1)}>Go Back</button>
            <button onClick={() => navigate("/parent/uniform")}>View Uniforms</button>
          </div>
        </div>

        <style>{`
          .detail-empty-state { min-height: 70vh; display: flex; align-items: center; justify-content: center; padding: 24px; background: #f8fafc; }
          .detail-empty-card { width: min(420px, 100%); background: white; border: 1px solid #e2e8f0; border-radius: 12px; padding: 28px; text-align: center; box-shadow: 0 12px 30px rgba(15, 23, 42, 0.08); }
          .detail-empty-card h2 { margin: 0 0 10px; color: #111827; font-size: 1.6rem; }
          .detail-empty-card p { margin: 0 0 22px; color: #64748b; }
          .detail-empty-actions { display: flex; gap: 12px; justify-content: center; flex-wrap: wrap; }
          .detail-empty-actions button { border: none; background: #097969; color: white; border-radius: 8px; padding: 10px 16px; font-weight: 700; }
          .detail-empty-actions button:first-child { background: #111827; }
        `}</style>
      </div>
    );
  }

  const galleryImages = getUniformImages(uniform);
  const productImage = galleryImages[0] || "https://via.placeholder.com/700x900?text=No+Image";
  const displayImage = activeImage || productImage;
  const isOutOfStock =
    uniform.status === "outofstock" || uniform.stock <= 0 || uniform.isAvailable === false;

  return (
    <div className="studio-detail-page">
      <div className="container-fluid p-0">
        <div className="row g-0 min-vh-100">
          
          {/* 🖼️ Left Side: Visual Gallery (Side Thumbnails like image_e3a99d) */}
          <div className="col-lg-7 d-flex sticky-image-section">
            <button className="floating-back-btn" onClick={() => navigate(-1)}>
              <FaArrowLeft />
            </button>

            {/* Vertical Thumbnail List */}
            <div className="thumbnail-gallery d-none d-md-flex">
              {(galleryImages.length > 0 ? galleryImages : [productImage]).map((imageUrl, index) => (
                <div 
                  key={imageUrl} 
                  className={`thumb-wrapper ${displayImage === imageUrl ? 'active' : ''}`}
                  onClick={() => setActiveImage(imageUrl)}
                >
                  <img src={imageUrl} alt={`${uniform.name} thumbnail ${index + 1}`} />
                </div>
              ))}
            </div>

            {/* Main Image Display */}
            <div className="product-visual-container flex-grow-1">
              <img src={displayImage} alt={uniform.name} className="hero-product-image"/>
            </div>
          </div>

          {/* 📝 Right Side: Checkout Experience */}
          <div className="col-lg-5 checkout-side">
            <div className="checkout-content">
              <header className="mb-4">
                <div className="stock-status mb-2">
                  <span className={isOutOfStock ? "status-out" : "status-in"}>
                    {isOutOfStock ? "OUT OF STOCK" : "NEW ARRIVAL"}
                  </span>
                </div>
                <h1 className="product-title">{uniform.name}</h1>
                <div className="price-display mt-3">
                  <span className="amount">₹{uniform.price}</span>
                </div>
              </header>

              <section className="product-description mb-5">
                <h6 className="section-title">Description</h6>
                <p className="description-text">{uniform.description || "Premium quality school uniform designed for comfort and long-lasting wear."}</p>
              </section>

              <section className="interaction-zone">
                <div className="size-selector mb-4">
                  <h6 className="section-title">Select Size</h6>
                  <div className="size-options">
                    {["22","24","26","28","30","32","34","36","38","40","42","44"].map((s) => (
                      <div key={s} className="size-item">
                        <input type="radio" name="size" id={`size-${s}`} hidden onChange={() => setSize(s)} checked={size === s} />
                        <label htmlFor={`size-${s}`}>{s}</label>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="action-row">
                  <div className="qty-picker">
                    <button onClick={() => setQuantity(Math.max(1, quantity - 1))}>−</button>
                    <span>{quantity}</span>
                    <button onClick={() => setQuantity(quantity + 1)}>+</button>
                  </div>
                  
                  <button className="main-cta-btn" onClick={addToCart} disabled={isOutOfStock}>
                    {isOutOfStock ? "Sold Out" : "Add to Bag"}
                    <FaShoppingBag className="ms-2" />
                  </button>
                </div>
              </section>

              <div className="trust-footer mt-5 pt-4 border-top">
                <div className="row g-3">
                  <div className="col-6">
                    <div className="trust-item"><FaTruck className="icon" /> <div><h6>Free Delivery</h6><p>Above ₹999</p></div></div>
                  </div>
                  <div className="col-6">
                    <div className="trust-item"><FaUndoAlt className="icon" /> <div><h6>10 Days Return</h6><p>Easy exchange</p></div></div>
                  </div>
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>

      <style>{`
        .studio-detail-page { background: #fff; }
        .sticky-image-section { background: #f9f9f9; height: 100vh; position: sticky; top: 0; padding-left: 20px; }
        
        /* Thumbnail Gallery Styles */
        .thumbnail-gallery { width: 100px; flex-direction: column; gap: 15px; justify-content: center; padding: 20px 0; }
        .thumb-wrapper { width: 70px; height: 90px; border: 1px solid #ddd; background: #fff; cursor: pointer; overflow: hidden; border-radius: 4px; transition: 0.3s; }
        .thumb-wrapper img { width: 100%; height: 100%; object-fit: cover; }
        .thumb-wrapper.active { border: 2px solid #000; }
        .thumb-wrapper:hover { border-color: #000; }

        .product-visual-container { display: flex; align-items: center; justify-content: center; position: relative; }
        .hero-product-image { max-width: 90%; max-height: 85vh; object-fit: contain; }
        
        .floating-back-btn { position: absolute; top: 20px; left: 20px; border: none; background: #fff; width: 45px; height: 45px; border-radius: 50%; box-shadow: 0 4px 12px rgba(0,0,0,0.1); z-index: 100; }
        
        .checkout-side { padding: 60px 4%; }
        .product-title { font-size: 2.5rem; font-weight: 700; color: #111; letter-spacing: -1px; }
        .amount { font-size: 2rem; font-weight: 600; color: #097969; }
        
        .section-title { text-uppercase; font-size: 0.75rem; font-weight: 800; color: #888; letter-spacing: 1px; margin-bottom: 15px; }
        .description-text { color: #555; line-height: 1.6; font-size: 0.95rem; }

        .size-options { display: flex; gap: 10px; flex-wrap: wrap; }
        .size-item label { width: 60px; height: 50px; border: 1.5px solid #eee; display: flex; align-items: center; justify-content: center; cursor: pointer; font-weight: 600; border-radius: 6px; transition: 0.2s; }
        .size-item input:checked + label { background: #000; color: #fff; border-color: #000; }

        .action-row { display: flex; gap: 15px; margin-top: 30px; }
        .qty-picker { display: flex; align-items: center; background: #f5f5f5; border-radius: 8px; padding: 5px 15px; }
        .qty-picker button { border: none; background: transparent; font-size: 1.5rem; width: 30px; }
        .qty-picker span { font-weight: 700; min-width: 30px; text-align: center; }

        .main-cta-btn { flex: 1; background: #097969; color: white; border: none; border-radius: 8px; font-weight: 700; transition: 0.3s; }
        .main-cta-btn:hover:not(:disabled) { background: #076154; transform: translateY(-1px); }

        .trust-item { display: flex; gap: 12px; align-items: center; }
        .trust-item .icon { font-size: 1.1rem; color: #444; }
        .trust-item h6 { margin: 0; font-size: 0.85rem; font-weight: 700; }
        .trust-item p { margin: 0; font-size: 0.75rem; color: #777; }

        .loader-container { height: 100vh; display: flex; align-items: center; justify-content: center; }
        .pulse-loader { width: 40px; height: 40px; background: #097969; border-radius: 50%; animation: pulse 1.2s infinite; }
        @keyframes pulse { 0% { transform: scale(0.1); opacity: 1; } 100% { transform: scale(1); opacity: 0; } }

        @media (max-width: 991px) {
          .sticky-image-section { height: auto; position: relative; padding: 40px 10px; }
          .thumbnail-gallery { display: none !important; }
          .hero-product-image { max-height: 50vh; }
          .checkout-side { padding: 40px 20px; }
        }
      `}</style>
    </div>
  );
}

export default UniformDetail;
